/**
 * Provisjons-netting (VIB-1240, retning B — Erik 15.07).
 *
 * bf_actual er LIVE overalt; det utbetalte grunnlaget er frosset i
 * `commission_payouts` (fanget når commission_locked_at settes). Denne rene
 * lesemodulen beregner deltaet et sent bilag/faktura skaper ETTER lukking:
 *
 *   delta = provisjon(live BF-grunnlag) − provisjon(utbetalt grunnlag)
 *
 * Netting gjelder BEGGE veier (Erik 15.07): negativt delta = trekk i neste
 * kjøring, positivt delta = etterbetaling. Splitt-beregningen gjenbruker den
 * delte calcCommissionSplits — aldri en konkurrerende formel.
 *
 * Periodisering v1: deltaet er akkumulert «per nå» mot utbetalt grunnlag
 * (informasjonsgrunnlag for neste kjøring). Kjøring-for-kjøring-ledger
 * vurderes sammen med PO-push (bølge 1b).
 */
import { calcCommissionSplits } from "./billing-calculations";
import { DEFAULT_PROVISION_RATE_PCT } from "./constants";

/** Rå rad: frosset grunnlag per prosjekt × eier + prosjektets live BF. */
export interface CommissionPayoutRow {
  project_id: string;
  project_code: string | null;
  project_name: string;
  locked_at: string;
  bf_actual_at_lock: number;
  live_bf_actual: number;
  commission_rate_pct: number | null;
  user_id: string;
  full_name: string | null;
  split_type: "percent" | "fixed";
  ownership_pct: number | null;
  fixed_amount: number | null;
}

export interface CommissionDeltaRow {
  project_id: string;
  project_code: string | null;
  project_name: string;
  user_id: string;
  full_name: string;
  locked_at: string;
  paidBfKr: number;
  liveBfKr: number;
  rate_pct: number;
  /** Delta i eierens BF-andel (live − utbetalt). */
  bfShareDeltaKr: number;
  /** Delta i provisjon (live − utbetalt). Negativ = trekk, positiv = etterbetaling. */
  provisionDeltaKr: number;
}

export interface CommissionNettingResult {
  rows: CommissionDeltaRow[];
  /** user_id → netto delta-provisjon over alle prosjekter. */
  netByUser: Map<string, number>;
}

/** Delta under 1 kr regnes som støy (avrundingsrester). */
const MIN_DELTA_KR = 1;

export function buildCommissionDeltas(rows: CommissionPayoutRow[]): CommissionNettingResult {
  const byProject = new Map<string, CommissionPayoutRow[]>();
  for (const r of rows) {
    const arr = byProject.get(r.project_id);
    if (arr) arr.push(r);
    else byProject.set(r.project_id, [r]);
  }

  const out: CommissionDeltaRow[] = [];
  for (const owners of byProject.values()) {
    const first = owners[0];
    const ratePct = first.commission_rate_pct ?? DEFAULT_PROVISION_RATE_PCT;
    const rate = ratePct / 100;
    const splitInput = owners.map((o) => ({
      user_id: o.user_id,
      split_type: o.split_type,
      ownership_pct: o.ownership_pct,
      fixed_amount: o.fixed_amount,
    }));
    const paidSplits = calcCommissionSplits(first.bf_actual_at_lock, splitInput, rate);
    const liveSplits = calcCommissionSplits(first.live_bf_actual, splitInput, rate);
    const paidByUser = new Map(paidSplits.map((s) => [s.user_id, s]));

    for (const live of liveSplits) {
      const paid = paidByUser.get(live.user_id);
      const provisionDeltaKr = live.provision - (paid?.provision ?? 0);
      if (Math.abs(provisionDeltaKr) < MIN_DELTA_KR) continue;
      const owner = owners.find((o) => o.user_id === live.user_id)!;
      out.push({
        project_id: first.project_id,
        project_code: first.project_code,
        project_name: first.project_name,
        user_id: live.user_id,
        full_name: owner.full_name ?? "Ukjent",
        locked_at: first.locked_at,
        paidBfKr: first.bf_actual_at_lock,
        liveBfKr: first.live_bf_actual,
        rate_pct: ratePct,
        bfShareDeltaKr: live.bf_share - (paid?.bf_share ?? 0),
        provisionDeltaKr,
      });
    }
  }

  out.sort((a, b) => Math.abs(b.provisionDeltaKr) - Math.abs(a.provisionDeltaKr));
  const netByUser = new Map<string, number>();
  for (const r of out) {
    netByUser.set(r.user_id, (netByUser.get(r.user_id) ?? 0) + r.provisionDeltaKr);
  }
  return { rows: out, netByUser };
}
